import { useEffect, useState } from "react";
import favicon from "./assets/favicon.png";

const DISMISSED_AT_KEY = "wmf-hub-install-promotion-dismissed-at";
const DISMISS_FOR_MS = 30 * 24 * 60 * 60 * 1000;

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform: string }>;
}

type Promotion =
  | { kind: "native"; prompt: BeforeInstallPromptEvent }
  | { kind: "ios" }
  | { kind: "manual" }
  | null;

export function isIosDevice(userAgent: string, maxTouchPoints: number): boolean {
  return /iPad|iPhone|iPod/i.test(userAgent)
    || (/Macintosh/i.test(userAgent) && maxTouchPoints > 1);
}

function isMobileDevice(): boolean {
  return /Android/i.test(navigator.userAgent)
    || isIosDevice(navigator.userAgent, navigator.maxTouchPoints);
}

function isStandalone(): boolean {
  const navigatorWithStandalone = navigator as Navigator & { standalone?: boolean };
  return navigatorWithStandalone.standalone === true
    || window.matchMedia?.("(display-mode: standalone)").matches === true;
}

function recentlyDismissed(): boolean {
  try {
    const dismissedAt = Number(window.localStorage.getItem(DISMISSED_AT_KEY));
    return Number.isFinite(dismissedAt)
      && dismissedAt > 0
      && Date.now() - dismissedAt < DISMISS_FOR_MS;
  } catch {
    return false;
  }
}

function rememberDismissal(): void {
  try {
    window.localStorage.setItem(DISMISSED_AT_KEY, String(Date.now()));
  } catch {
    // Storage can be unavailable in private browsing; hiding it for this page
    // is still preferable to making the install suggestion mandatory.
  }
}

const TITLES = {
  native: "Install WMF Hub",
  ios: "Add WMF Hub to your Home Screen",
  manual: "Install WMF Hub from your browser menu",
} as const;

const DESCRIPTIONS = {
  native: "Open the Hub from your Home Screen in its own app window.",
  ios: "Tap Share, then choose Add to Home Screen.",
  manual: "The install window could not be opened. Open your browser's menu and choose Install app.",
} as const;

export function InstallPromotion() {
  const [promotion, setPromotion] = useState<Promotion>(null);

  useEffect(() => {
    if (isStandalone() || !isMobileDevice()) return;

    if (isIosDevice(navigator.userAgent, navigator.maxTouchPoints) && !recentlyDismissed()) {
      setPromotion({ kind: "ios" });
    }

    const beforeInstall = (event: Event) => {
      const installEvent = event as BeforeInstallPromptEvent;
      // Keep the native prompt tied to the explicit Install button below.
      installEvent.preventDefault();
      if (!recentlyDismissed()) setPromotion({ kind: "native", prompt: installEvent });
    };
    const installed = () => setPromotion(null);

    window.addEventListener("beforeinstallprompt", beforeInstall);
    window.addEventListener("appinstalled", installed);
    return () => {
      window.removeEventListener("beforeinstallprompt", beforeInstall);
      window.removeEventListener("appinstalled", installed);
    };
  }, []);

  if (!promotion) return null;

  const dismiss = () => {
    rememberDismissal();
    setPromotion(null);
  };

  const install = async () => {
    if (promotion.kind !== "native") return;
    const installPrompt = promotion.prompt;
    setPromotion(null);
    try {
      await installPrompt.prompt();
      const choice = await installPrompt.userChoice;
      if (choice.outcome === "dismissed") rememberDismissal();
    } catch {
      // A captured prompt may only be prompted once, so a failure spends it:
      // offering the same event again would fail identically for ever. Drop it
      // and name the route that still works. A fresh `beforeinstallprompt`
      // restores the button on its own.
      setPromotion({ kind: "manual" });
    }
  };

  return (
    <aside className="install-promotion" aria-label="Install WMF Hub" data-bottom-obstruction="prompt">
      <img className="install-promotion-icon" src={favicon} alt="" aria-hidden="true" />
      <span className="install-promotion-copy">
        <strong>{TITLES[promotion.kind]}</strong>
        <span {...(promotion.kind === "manual" ? { role: "alert" as const } : {})}>
          {DESCRIPTIONS[promotion.kind]}
        </span>
      </span>
      <span className="install-promotion-actions">
        {promotion.kind === "native" && (
          <button className="primary" type="button" onClick={() => void install()}>
            Install
          </button>
        )}
        <button className="quiet" type="button" onClick={dismiss}>
          {promotion.kind === "native" ? "Not now" : "Got it"}
        </button>
      </span>
    </aside>
  );
}
